/**
 * Editor Tour Types
 *
 * A three step walkthrough shown once, the first time a user opens a project
 * editor. It replaces the old floating onboarding checklist.
 *
 * The tour deliberately holds no completion or progress data about the user's
 * project. It explains where three controls are and what they do, so it never
 * has to infer whether an app is "ready" for anything.
 */

/** Step identifiers, in the order they are shown. */
export const TOUR_STEPS = ['composer', 'preview', 'publish'] as const;

export type TourStepId = (typeof TOUR_STEPS)[number];

/** Total number of steps, used for the "n of N" counter. */
export const TOUR_STEP_COUNT = TOUR_STEPS.length;

/**
 * Tour state stored in users.context.tour (JSONB).
 * Absent entirely for users who have not opened an editor yet.
 */
export interface TourProgress {
  /** Zero based index of the step the user is currently on. */
  step?: number;

  /** ISO timestamp, set when the user reaches the end of the last step. */
  completedAt?: string;

  /** ISO timestamp, set when the user skips. Skipping ends the whole tour. */
  skippedAt?: string;
}

/** API response for GET /api/users/me/tour */
export interface TourResponse {
  /** False once completed or skipped, or for users who predate the feature. */
  showTour: boolean;

  /** Step to resume on. Always 0 when showTour is false. */
  step: number;

  completedAt: string | null;
  skippedAt: string | null;
}

/** Request body for PATCH /api/users/me/tour */
export interface UpdateTourDto {
  /** Zero based step index to persist, so a reload resumes in place. */
  step?: number;

  /** Ends the tour as finished. */
  completed?: boolean;

  /** Ends the tour as skipped. */
  skipped?: boolean;
}

export const DEFAULT_TOUR_RESPONSE: TourResponse = {
  showTour: false,
  step: 0,
  completedAt: null,
  skippedAt: null,
};
